import * as React from 'react'
import { PreviewCard as PreviewCardPrimitive } from '@base-ui/react/preview-card'

import { cn } from '@/registry/reapit/lib/utils'
import { asChildProps } from '@/registry/reapit/lib/as-child-props'

// ---------------------------------------------------------------------------
// HoverCard — migrated from Radix to Base UI
// ---------------------------------------------------------------------------
// Base UI calls this "PreviewCard" — import path: @base-ui/react/preview-card
//
// Structural changes:
//   Radix: Portal > Content
//   Base UI: Portal > Positioner > Popup
//
// CSS variable rename:
//   --radix-hover-card-content-transform-origin → --transform-origin
//
// Data attribute changes:
//   data-[state=open/closed] → data-[open] / data-[starting-style] / data-[ending-style]
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// HoverCard Root — Radix API shim
// ---------------------------------------------------------------------------
// Radix HoverCard.Root has `openDelay` and `closeDelay` props on Root.
// Base UI PreviewCard puts `delay` and `closeDelay` on the Trigger.
// We use React context to forward the delay values to the Trigger.
// ---------------------------------------------------------------------------

const HoverCardDelayContext = React.createContext<{ openDelay?: number; closeDelay?: number }>({})

function HoverCard({
  openDelay,
  closeDelay,
  ...props
}: React.ComponentProps<typeof PreviewCardPrimitive.Root> & {
  /** Delay in ms before showing the card (forwarded to Trigger) */
  openDelay?: number
  /** Delay in ms before hiding the card (forwarded to Trigger) */
  closeDelay?: number
}) {
  const delayValue = React.useMemo(() => ({ openDelay, closeDelay }), [openDelay, closeDelay])
  return (
    <HoverCardDelayContext.Provider value={delayValue}>
      <PreviewCardPrimitive.Root data-slot="hover-card" {...props} />
    </HoverCardDelayContext.Provider>
  )
}

function HoverCardTrigger({
  asChild,
  children,
  delay: delayProp,
  closeDelay: closeDelayProp,
  ...props
}: Omit<React.ComponentProps<typeof PreviewCardPrimitive.Trigger>, 'render'> & {
  asChild?: boolean
  delay?: number
  closeDelay?: number
}) {
  const ctx = React.useContext(HoverCardDelayContext)
  // Trigger-level props take precedence over Root-level context
  const delay = delayProp ?? ctx.openDelay
  const closeDelay = closeDelayProp ?? ctx.closeDelay
  return (
    <PreviewCardPrimitive.Trigger
      data-slot="hover-card-trigger"
      delay={delay}
      closeDelay={closeDelay}
      {...asChildProps(asChild, children)}
      {...props}
    />
  )
}

function HoverCardContent({
  className,
  align = 'center',
  // DS: floating surfaces use an 8 px gap from their trigger.
  sideOffset = 8,
  side = 'bottom',
  children,
  ...props
}: {
  className?: string
  sideOffset?: number
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  children?: React.ReactNode
} & Omit<React.ComponentProps<typeof PreviewCardPrimitive.Popup>, 'className' | 'children'>) {
  return (
    <PreviewCardPrimitive.Portal>
      <PreviewCardPrimitive.Positioner side={side} sideOffset={sideOffset} align={align}>
        <PreviewCardPrimitive.Popup
          data-slot="hover-card-content"
          className={cn(
            // DS: bg-popover surface, --border, radius-lg (8 px), shadow-md.
            // Base UI: --transform-origin replaces --radix-hover-card-content-transform-origin
            // Transition-based animation via data-[starting-style] / data-[ending-style]
            'z-50 w-64 origin-[var(--transform-origin)] rounded-lg border bg-popover p-4 text-popover-foreground shadow-md outline-hidden transition-[opacity,scale] duration-150 scale-100 opacity-100 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
            className,
          )}
          {...props}
        >
          {children}
        </PreviewCardPrimitive.Popup>
      </PreviewCardPrimitive.Positioner>
    </PreviewCardPrimitive.Portal>
  )
}

export { HoverCard, HoverCardTrigger, HoverCardContent }
